"use client"

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ErrorBar,
} from "recharts"
import { type ChartDimension } from "./types"
import type { CSSProperties } from "react"

/** A single OHLC candle. */
export interface CandleRow {
  name: string
  open: number
  high: number
  low: number
  close: number
}

export interface CandlestickChartProps {
  data: CandleRow[]
  upColor?: string
  downColor?: string
  showLegend?: boolean
  showGrid?: boolean
  showTooltip?: boolean
  xLabel?: string
  yLabel?: string
  className?: string
  style?: CSSProperties
  height?: ChartDimension
  width?: ChartDimension
}

export function CandlestickChart({
  data, upColor = "#10b981", downColor = "#ef4444",
  showLegend = false, showGrid = true, showTooltip = true,
  xLabel, yLabel, className, style, height = "100%", width = "100%",
}: CandlestickChartProps) {
  // The candle body is a stacked bar from min(open,close) to max(open,close);
  // wicks come from ErrorBar (high above, low below the body).
  const rows = data.map(r => {
    const bodyLow = Math.min(r.open, r.close)
    const bodyHigh = Math.max(r.open, r.close)
    const isUp = r.close >= r.open
    return {
      name: r.name,
      base: bodyLow,
      body: bodyHigh - bodyLow || 0.0001,
      wickHigh: r.high - bodyHigh,
      wickLow: bodyLow - r.low,
      color: isUp ? upColor : downColor,
    }
  })

  return (
    <ResponsiveContainer width={width} height={height} className={className} style={style}>
      <BarChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.18} />}
        <XAxis dataKey="name" tick={{ fontSize: 10.5 }} label={xLabel ? { value: xLabel, position: "insideBottom", offset: -4 } : undefined} interval={0} angle={-15} dy={6} height={50} />
        <YAxis tick={{ fontSize: 10.5 }} label={yLabel ? { value: yLabel, angle: -90, position: "insideLeft" } : undefined} domain={["auto", "auto"]} />
        {showTooltip && <Tooltip cursor={{ fill: "hsl(var(--foreground) / 0.06)", stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.35 }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11, color: "hsl(var(--card-foreground))" }} />}
        {showLegend && <Legend wrapperStyle={{ fontSize: 11 }} />}
        <Bar dataKey="base" stackId="c" fill="transparent" isAnimationActive={false} legendType="none" />
        <Bar dataKey="body" stackId="c" isAnimationActive={false} radius={1}>
          {rows.map((row, i) => <Cell key={i} fill={row.color} />)}
          <ErrorBar dataKey="wickHigh" width={6} stroke="currentColor" strokeWidth={1} direction="y" />
          <ErrorBar dataKey="wickLow"  width={6} stroke="currentColor" strokeWidth={1} direction="y" />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}
